import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../../lib/api';
import { useCreatorMe } from '../../../lib/auth';
import { useToast } from '../../../lib/toast';
import { formatDate } from '../../../lib/format';
import styles from './Hub.module.css';

export function PaymentView() {
  const { data } = useCreatorMe();
  if (!data) return null;
  const status = data.payment_status || 'pending';
  const amount = data.payment_amount ?? 300;
  const method = data.payment_method || '';
  const notes = data.payment_notes || '';
  const isPaid = status === 'paid';
  const isProcessing = status === 'processing';
  const videoApproved = data.video_status === 'approved';

  const statusBadge = isPaid ? (
    <span className={`${styles.badge} ${styles.badgeGreen}`}>Payment Sent</span>
  ) : isProcessing ? (
    <span className={`${styles.badge} ${styles.badgeBlue}`}>Processing</span>
  ) : videoApproved ? (
    <span className={`${styles.badge} ${styles.badgeOrange}`}>Pending Payment</span>
  ) : (
    <span className={`${styles.badge} ${styles.badgeGray}`}>Awaiting Video Approval</span>
  );

  const statusMsg = isPaid
    ? 'Your payment has been sent. Thank you for your partnership with Kick Health.'
    : isProcessing
      ? 'Your payment is being processed. You should receive it within 5–7 business days.'
      : videoApproved
        ? 'Your video has been approved. Your payment is being prepared.'
        : 'Payment will be issued once your video draft has been reviewed and approved by the Kick Health team.';

  return (
    <>
      <div className={styles.pageActionRow} style={{ marginBottom: 20 }}>
        <div>
          <div className={styles.pageTitle}>Payment</div>
          <div className={styles.pageSubtitle}>
            Your payment status for this Kick Health partnership.
          </div>
        </div>
        {statusBadge}
      </div>
      <div className={styles.statusRow}>
        <span className={styles.statusRowLabel}>{statusMsg}</span>
      </div>
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardSectionTitle}>Payment Summary</div>
        </div>
        <div className={styles.cardBody}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px 28px' }}>
            <div className={styles.fitem}>
              <div className={styles.fieldLabel}>Amount</div>
              <div
                className={styles.fieldValue}
                style={{
                  fontSize: 26,
                  fontWeight: 800,
                  letterSpacing: '-0.5px',
                  color: '#111827',
                }}
              >
                ${amount}
              </div>
            </div>
            <div className={styles.fitem}>
              <div className={styles.fieldLabel}>Status</div>
              <div className={styles.fieldValue}>
                {isPaid ? 'Paid' : isProcessing ? 'Processing' : 'Pending'}
              </div>
            </div>
            {isPaid && data.payment_date && (
              <div className={styles.fitem}>
                <div className={styles.fieldLabel}>Date Paid</div>
                <div className={styles.fieldValue}>{formatDate(data.payment_date)}</div>
              </div>
            )}
            {method && (
              <div className={styles.fitem}>
                <div className={styles.fieldLabel}>Payment Method</div>
                <div className={styles.fieldValue}>{method}</div>
              </div>
            )}
          </div>
        </div>
      </div>
      {notes && (
        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardSectionTitle}>Note from Kick Health</div>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.intakeInstr}>{notes}</div>
          </div>
        </div>
      )}
      <PaypalInfoCard />
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardSectionTitle}>How payments work</div>
        </div>
        <div className={styles.cardBody}>
          <ol style={{ paddingLeft: 18, fontSize: 13.5, color: '#374151', lineHeight: 1.6 }}>
            <li>Complete all onboarding steps: contract, medical intake, and brand guidelines.</li>
            <li>Submit your video draft via the Video Submission tab for review.</li>
            <li>
              Add your PayPal details below so we know where to send your payment.
            </li>
            <li>Once your video is approved, payment will be issued within 5–7 business days.</li>
          </ol>
        </div>
      </div>
    </>
  );
}

function PaypalInfoCard() {
  const { data } = useCreatorMe();
  const qc = useQueryClient();
  const toast = useToast();
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    paypal_email: '',
    paypal_recipient_name: '',
    paypal_phone: '',
  });
  const [err, setErr] = useState('');

  useEffect(() => {
    if (data) {
      setForm({
        paypal_email: data.paypal_email ?? '',
        paypal_recipient_name: data.paypal_recipient_name ?? '',
        paypal_phone: data.paypal_phone ?? '',
      });
      setErr('');
    }
  }, [data, edit]);

  const save = useMutation({
    mutationFn: (body: typeof form) => api.post('/api/creator/payment-info', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['creator', 'me'] });
      toast('Payment details saved');
      setEdit(false);
    },
    onError: (e) => {
      setErr(e instanceof ApiError ? e.message : 'Could not save — please try again');
    },
  });

  function commit() {
    setErr('');
    save.mutate({
      paypal_email: form.paypal_email.trim(),
      paypal_recipient_name: form.paypal_recipient_name.trim(),
      paypal_phone: form.paypal_phone.trim(),
    });
  }

  if (!data) return null;
  const email = data.paypal_email;
  const name = data.paypal_recipient_name;
  const phone = data.paypal_phone;

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <div>
          <div className={styles.cardSectionTitle}>Where to send your payment</div>
          <div style={{ color: '#9ca3af', fontSize: 13, marginTop: 2 }}>
            We pay via PayPal. Add your details here so we know where to send it — you can update
            this any time.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {edit ? (
            <>
              <button type="button" className={styles.btnGhost} onClick={() => setEdit(false)}>
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={commit}
                disabled={save.isPending}
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
            </>
          ) : (
            <button type="button" className={styles.btnGhost} onClick={() => setEdit(true)}>
              {email || name || phone ? 'Edit' : 'Add details'}
            </button>
          )}
        </div>
      </div>
      <div className={styles.cardBody}>
        {edit && err && (
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b',
              padding: '10px 14px',
              borderRadius: 8,
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            {err}
          </div>
        )}
        <div className={styles.fgrid}>
          <div className={`${styles.fitem} ${styles.fitemFull}`}>
            <div className={styles.fieldLabel}>Legal name on your PayPal account</div>
            {edit ? (
              <input
                className={styles.fieldInput}
                value={form.paypal_recipient_name}
                onChange={(e) => setForm({ ...form, paypal_recipient_name: e.target.value })}
                placeholder="As it appears on PayPal"
              />
            ) : (
              <div className={`${styles.fieldValue} ${!name ? styles.fieldEmpty : ''}`}>
                {name || '—'}
              </div>
            )}
          </div>
          <div className={styles.fitem}>
            <div className={styles.fieldLabel}>PayPal email</div>
            {edit ? (
              <input
                type="email"
                className={styles.fieldInput}
                value={form.paypal_email}
                onChange={(e) => setForm({ ...form, paypal_email: e.target.value })}
                placeholder="you@example.com"
                autoComplete="email"
              />
            ) : (
              <div className={`${styles.fieldValue} ${!email ? styles.fieldEmpty : ''}`}>
                {email || '—'}
              </div>
            )}
          </div>
          <div className={styles.fitem}>
            <div className={styles.fieldLabel}>PayPal phone (optional)</div>
            {edit ? (
              <input
                type="tel"
                className={styles.fieldInput}
                value={form.paypal_phone}
                onChange={(e) => setForm({ ...form, paypal_phone: e.target.value })}
                placeholder="If your PayPal uses a phone number"
                autoComplete="tel"
              />
            ) : (
              <div className={`${styles.fieldValue} ${!phone ? styles.fieldEmpty : ''}`}>
                {phone || '—'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
